import { ReactNode, createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  PrivyProvider,
  usePrivy,
  useWallets,
  type PrivyClientConfig,
} from '@privy-io/react-auth';
import { ethers } from 'ethers';
import { DEFAULT_PRIVY_CONFIG } from './config';
import type { AuthContextValue, AuthUser } from './types';
import { WalletPicker } from './bypass/WalletPicker';
import type { InjectedWallet } from './bypass/useInjectedWallets';
import { BypassContext, type BypassConnectors } from './bypass/BypassContext';

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Hook defined in the same file as the Provider to avoid HMR Context identity issues.
 * (Re-exporting across files caused stale Context refs during Vite HMR in past builds.)
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <PrivyAuthProvider>');
  return ctx;
}

interface PrivyAuthProviderProps {
  children: ReactNode;
  /** Override Privy App ID (default: reads VITE_PRIVY_APP_ID) */
  appId?: string;
  /** Force dev bypass (default: reads VITE_BYPASS_AUTH === 'true' && MODE !== production) */
  bypassAuth?: boolean;
  /** Override / merge into the default Privy client config. */
  privyConfigOverride?: Partial<PrivyClientConfig>;
}

export function PrivyAuthProvider({
  children,
  appId,
  bypassAuth,
  privyConfigOverride,
}: PrivyAuthProviderProps) {
  const envAppId = (import.meta as any).env?.VITE_PRIVY_APP_ID as string | undefined;
  const envBypass = (import.meta as any).env?.VITE_BYPASS_AUTH === 'true';
  const isProduction = (import.meta as any).env?.MODE === 'production';

  if (isProduction && (bypassAuth ?? envBypass)) {
    throw new Error('FATAL: bypassAuth cannot be enabled in production build');
  }

  const shouldBypass = !isProduction && (bypassAuth ?? envBypass);
  const finalAppId = appId ?? envAppId;

  if (shouldBypass) {
    return <NativeBypassAuthBridge>{children}</NativeBypassAuthBridge>;
  }

  if (!finalAppId) {
    return (
      <div style={errBoxStyle}>
        Missing <code>VITE_PRIVY_APP_ID</code>. Set it in <code>.env</code> or pass <code>appId</code> prop.
      </div>
    );
  }

  const finalConfig: PrivyClientConfig = {
    ...DEFAULT_PRIVY_CONFIG,
    ...privyConfigOverride,
    appearance: {
      ...DEFAULT_PRIVY_CONFIG.appearance,
      ...privyConfigOverride?.appearance,
    },
    embeddedWallets: {
      ...DEFAULT_PRIVY_CONFIG.embeddedWallets,
      ...privyConfigOverride?.embeddedWallets,
    },
  };

  return (
    <PrivyProvider appId={finalAppId} config={finalConfig}>
      <PrivyAuthBridge>{children}</PrivyAuthBridge>
    </PrivyProvider>
  );
}

/** Production / normal mode: requires Privy SIWE login + JWT. */
function PrivyAuthBridge({ children }: { children: ReactNode }) {
  const { ready, authenticated, login, logout, getAccessToken, user } = usePrivy();
  const { wallets } = useWallets();

  const authUser: AuthUser | null =
    authenticated && user ? buildUser({ did: user.id, wallets }) : null;

  const signMessage = useCallback(
    async (msg: string) => {
      if (!wallets[0]) throw new Error('No wallet connected');
      const eip1193 = await wallets[0].getEthereumProvider();
      const provider = new ethers.BrowserProvider(eip1193);
      const signer = await provider.getSigner();
      return await signer.signMessage(msg);
    },
    [wallets],
  );

  const value: AuthContextValue = {
    ready,
    authenticated,
    user: authUser,
    login,
    logout,
    getAccessToken,
    signMessage,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Dev bypass: skip Privy SIWE/JWT/allowlist, but still mount Privy SDK so
 * both picker paths are available:
 *  - **EIP-6963 picker** — our own, no Privy SDK call, works offline.
 *  - **Privy picker** — opened via `useConnectWallet` (no SIWE), supports
 *    WalletConnect / mobile scan / all the Privy-integrated providers.
 *
 * `authenticated` is true once either path produced a connected wallet.
 * `getAccessToken` returns a fake token (pair with `BYPASS_AUTH=true` on backend).
 */
const LOGOUT_FLAG_KEY = 'ops-hub:bypass-logged-out';

function readLogoutFlag(): boolean {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(LOGOUT_FLAG_KEY) === 'true';
}
function writeLogoutFlag(value: boolean) {
  if (typeof window === 'undefined') return;
  if (value) window.sessionStorage.setItem(LOGOUT_FLAG_KEY, 'true');
  else window.sessionStorage.removeItem(LOGOUT_FLAG_KEY);
}

function NativeBypassAuthBridge({ children }: { children: ReactNode }) {
  const [showNativePicker, setShowNativePicker] = useState(false);
  const [nativeProvider, setNativeProvider] = useState<any>(null);
  const [nativeAddress, setNativeAddress] = useState<string | null>(null);
  const [nativeChainId, setNativeChainId] = useState<number | null>(null);
  const [manualLogout, setManualLogoutState] = useState<boolean>(readLogoutFlag);
  const setManualLogout = useCallback((value: boolean) => {
    setManualLogoutState(value);
    writeLogoutFlag(value);
  }, []);

  useEffect(() => {
    if (!nativeProvider) return;

    nativeProvider.request({ method: 'eth_accounts' })
      .then((accounts: string[]) => {
        if (accounts?.[0]) setNativeAddress(accounts[0]);
      })
      .catch(() => {});
    nativeProvider.request({ method: 'eth_chainId' })
      .then((id: string) => setNativeChainId(Number.parseInt(id, 16)))
      .catch(() => {});

    const onAccounts = (accounts: string[]) => setNativeAddress(accounts?.[0] ?? null);
    const onChain = (id: string) => setNativeChainId(Number.parseInt(id, 16));

    nativeProvider.on?.('accountsChanged', onAccounts);
    nativeProvider.on?.('chainChanged', onChain);
    return () => {
      nativeProvider.removeListener?.('accountsChanged', onAccounts);
      nativeProvider.removeListener?.('chainChanged', onChain);
    };
  }, [nativeProvider]);

  const address = manualLogout ? null : nativeAddress;
  const chainId = manualLogout ? null : nativeChainId;
  const connectInjected = useCallback(() => setShowNativePicker(true), []);
  const connectWalletConnect = useCallback(() => setShowNativePicker(true), []);

  const handleNativePick = useCallback(
    async (wallet: InjectedWallet) => {
      setShowNativePicker(false);
      setNativeProvider(wallet.provider);
      try {
        const accounts: string[] = await wallet.provider.request({ method: 'eth_requestAccounts' });
        if (accounts?.[0]) {
          setNativeAddress(accounts[0]);
          setManualLogout(false);
        }
      } catch (err) {
        console.warn('[auth] bypass native connect rejected', err);
      }
    },
    [setManualLogout],
  );

  const logout = useCallback(async () => {
    setManualLogout(true);
    setNativeAddress(null);
    setNativeChainId(null);
    if (nativeProvider) {
      try {
        await nativeProvider.request({
          method: 'wallet_revokePermissions',
          params: [{ eth_accounts: {} }],
        });
      } catch {
        /* wallet may not support EIP-2255 — fine */
      }
    }
    setNativeProvider(null);
    console.log('[auth] native bypass logout — state cleared');
  }, [nativeProvider, setManualLogout]);

  const signMessage = useCallback(
    async (msg: string) => {
      if (!nativeProvider || !nativeAddress) throw new Error('No wallet connected');
      return await nativeProvider.request({
        method: 'personal_sign',
        params: [msg, nativeAddress],
      });
    },
    [nativeProvider, nativeAddress],
  );

  const user: AuthUser | null = address
    ? {
        id: 'did:dev:bypass',
        address,
        allWallets: [address.toLowerCase()],
        chainId,
        isDevBypass: true,
      }
    : null;

  const authValue: AuthContextValue = {
    ready: true,
    authenticated: address != null,
    user,
    login: connectInjected,
    logout,
    getAccessToken: async () => 'dev-bypass-token',
    signMessage,
  };

  const bypassValue: BypassConnectors = {
    connectInjected,
    connectWalletConnect,
    privyReady: true,
  };

  return (
    <BypassContext.Provider value={bypassValue}>
      <AuthContext.Provider value={authValue}>
        {children}
        <WalletPicker
          open={showNativePicker}
          onClose={() => setShowNativePicker(false)}
          onPick={handleNativePick}
        />
      </AuthContext.Provider>
    </BypassContext.Provider>
  );
}

function buildUser({ did, wallets }: { did: string; wallets: any[] }): AuthUser {
  const primary = wallets[0];
  return {
    id: did,
    address: primary?.address || '',
    allWallets: wallets.map((w) => w.address.toLowerCase()),
    chainId: primary?.chainId ? Number(String(primary.chainId).replace('eip155:', '')) : null,
    isDevBypass: false,
  };
}

const errBoxStyle: React.CSSProperties = {
  padding: '24px',
  margin: '40px auto',
  maxWidth: 500,
  background: '#7f1d1d',
  color: 'white',
  borderRadius: 8,
  fontFamily: 'system-ui',
};
